import React, { useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'react-hot-toast';
import { useSupplierCrmOrgStore } from '../../../store/supplierCrmOrg.store';
import {
  inventoryService,
  transactionsService,
  alertsService,
  type InventoryItem,
  type InventoryAlert,
  type InventoryTransaction,
  type InventoryItemInput,
  type InventoryTransactionInput,
} from '../../../services/supplierCrm/inventoryService';
import BarcodeScanner from '../components/BarcodeScanner';

type ViewMode = 'table' | 'alerts' | 'transactions';
type TransactionType = 'in' | 'out' | 'adjustment';

const InventoryTab: React.FC = () => {
  const { activeOrgId } = useSupplierCrmOrgStore();
  const [viewMode, setViewMode] = useState<ViewMode>('table');
  const [loading, setLoading] = useState(true);
  const [inventoryItems, setInventoryItems] = useState<InventoryItem[]>([]);
  const [alerts, setAlerts] = useState<InventoryAlert[]>([]);
  const [transactions, setTransactions] = useState<InventoryTransaction[]>([]);
  const [search, setSearch] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showTransactionModal, setShowTransactionModal] = useState(false);
  const [showBarcodeScanner, setShowBarcodeScanner] = useState(false);
  const [selectedItem, setSelectedItem] = useState<InventoryItem | null>(null);
  const [transactionType, setTransactionType] = useState<TransactionType>('in');
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  // Form states
  const [formData, setFormData] = useState<Partial<InventoryItemInput>>({
    sku: '',
    current_stock: 0,
    reorder_point: 10,
    reorder_quantity: 50,
    location: 'Main Warehouse',
    unit_of_measure: 'unit',
    notes: '',
  });

  const [transactionData, setTransactionData] = useState<Partial<InventoryTransactionInput>>({
    quantity: 0,
    unit_cost: 0,
    reference_type: '',
    reference_id: '',
    notes: '',
  });

  const fetchInventoryData = useCallback(async () => {
    if (!activeOrgId) return;
    setLoading(true);
    try {
      const [inventoryRes, alertsRes, transactionsRes] = await Promise.all([
        inventoryService.list({ organization_id: activeOrgId }),
        alertsService.getActive(activeOrgId),
        transactionsService.list({ organization_id: activeOrgId, limit: 50 }),
      ]);

      if (inventoryRes.error) {
        toast.error('Failed to load inventory items');
      } else {
        setInventoryItems(inventoryRes.data);
      }

      if (!alertsRes.error) setAlerts(alertsRes.data);
      if (!transactionsRes.error) setTransactions(transactionsRes.data);
    } catch (error) {
      toast.error('Failed to load inventory data');
    } finally {
      setLoading(false);
    }
  }, [activeOrgId]);

  useEffect(() => {
    void fetchInventoryData();
  }, [fetchInventoryData]);

  const handleCreateInventoryItem = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!activeOrgId) return;

    try {
      const payload: InventoryItemInput = { organization_id: activeOrgId, ...formData } as InventoryItemInput;
      const { error } = await inventoryService.create(payload);
      if (error) {
        toast.error(error);
        return;
      }
      toast.success('Inventory item created');
      setShowAddModal(false);
      setFormData({ sku: '', current_stock: 0, reorder_point: 10, reorder_quantity: 50, location: 'Main Warehouse', unit_of_measure: 'unit', notes: '' });
      void fetchInventoryData();
    } catch (error) {
      toast.error('Failed to create inventory item');
    }
  };

  const handleRecordTransaction = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!activeOrgId || !selectedItem) return;

    try {
      const payload: InventoryTransactionInput = {
        organization_id: activeOrgId,
        inventory_item_id: selectedItem.id,
        transaction_type: transactionType,
        quantity: transactionData.quantity || 0,
        unit_cost: transactionData.unit_cost,
        reference_type: transactionData.reference_type,
        reference_id: transactionData.reference_id,
        notes: transactionData.notes,
      };
      const { error } = await transactionsService.create(payload);
      if (error) {
        toast.error(error);
        return;
      }
      toast.success(`Stock ${transactionType} recorded successfully`);
      setShowTransactionModal(false);
      setSelectedItem(null);
      setTransactionData({ quantity: 0, unit_cost: 0, reference_type: '', reference_id: '', notes: '' });
      void fetchInventoryData();
    } catch (error) {
      toast.error('Failed to record transaction');
    }
  };

  const handleAcknowledgeAlert = async (alertId: string) => {
    if (!activeOrgId) return;
    try {
      const { error } = await alertsService.acknowledge(alertId, activeOrgId);
      if (error) {
        toast.error(error);
        return;
      }
      toast.success('Alert acknowledged');
      void fetchInventoryData();
    } catch (error) {
      toast.error('Failed to acknowledge alert');
    }
  };

  const openTransactionModal = (item: InventoryItem, type: TransactionType) => {
    setSelectedItem(item);
    setTransactionType(type);
    setShowTransactionModal(true);
  };

  const handleBarcodeScanned = (barcode: string) => {
    setFormData({ ...formData, sku: barcode });
    toast.success(`Barcode scanned: ${barcode}`);
  };

  const exportInventoryCSV = () => {
    import('../../../utils/csvExport').then(({ downloadCSV, formatInventoryForCSV }) => {
      const csvData = formatInventoryForCSV(inventoryItems);
      downloadCSV(csvData, 'inventory_levels');
    });
  };

  const exportTransactionsCSV = () => {
    import('../../../utils/csvExport').then(({ downloadCSV, formatTransactionsForCSV }) => {
      const csvData = formatTransactionsForCSV(transactions);
      downloadCSV(csvData, 'inventory_transactions');
    });
  };

  const filteredItems = inventoryItems.filter(
    (item) =>
      !search ||
      item.sku?.toLowerCase().includes(search.toLowerCase()) ||
      item.notes?.toLowerCase().includes(search.toLowerCase())
  );

  const lowStockItems = inventoryItems.filter((item) => item.current_stock <= item.reorder_point);

  if (loading) {
    return <div className="rounded-xl bg-white p-12 text-center">Loading inventory...</div>;
  }

  return (
    <div className="space-y-6">
      {/* Low Stock Alert Banner */}
      {lowStockItems.length > 0 && (
        <div className="rounded-xl border border-red-200 bg-red-50 p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <span className="text-2xl">⚠️</span>
              <div>
                <h3 className="font-semibold text-red-900">Low Stock Alert</h3>
                <p className="text-sm text-red-700">{lowStockItems.length} items need reordering</p>
              </div>
            </div>
            <button onClick={() => setViewMode('alerts')} className="rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white hover:bg-red-700">
              View Alerts
            </button>
          </div>
        </div>
      )}

      {/* View Switcher */}
      <div className="flex items-center justify-between">
        <div className="flex gap-2 rounded-lg border border-gray-200 bg-white p-1">
          <button onClick={() => setViewMode('table')} className={`rounded px-4 py-2 text-sm font-medium ${viewMode === 'table' ? 'bg-primary text-white' : 'text-gray-700 hover:bg-gray-100'}`}>
            📦 Stock Levels
          </button>
          <button onClick={() => setViewMode('alerts')} className={`rounded px-4 py-2 text-sm font-medium ${viewMode === 'alerts' ? 'bg-primary text-white' : 'text-gray-700 hover:bg-gray-100'}`}>
            ⚠️ Alerts ({alerts.length})
          </button>
          <button onClick={() => setViewMode('transactions')} className={`rounded px-4 py-2 text-sm font-medium ${viewMode === 'transactions' ? 'bg-primary text-white' : 'text-gray-700 hover:bg-gray-100'}`}>
            📋 History
          </button>
        </div>

        {viewMode === 'table' && (
          <div className="flex items-center gap-3">
            <input
              type="text"
              placeholder="Search by SKU..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="rounded-lg border border-gray-300 px-4 py-2 text-sm"
            />
            <button onClick={exportInventoryCSV} className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50">
              📥 Export CSV
            </button>
            <button onClick={() => setShowAddModal(true)} className="rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-white hover:bg-primary/90">
              ➕ Add Item
            </button>
          </div>
        )}

        {viewMode === 'transactions' && (
          <button onClick={exportTransactionsCSV} className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50">
            📥 Export CSV
          </button>
        )}
      </div>

      {/* Stock Levels Table */}
      {viewMode === 'table' && (
        <div className="overflow-hidden rounded-xl bg-white shadow-sm ring-1 ring-gray-100">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase text-gray-500">SKU</th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase text-gray-500">Product</th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase text-gray-500">Current Stock</th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase text-gray-500">Reorder Point</th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase text-gray-500">Location</th>
                <th className="px-6 py-3 text-right text-xs font-medium uppercase text-gray-500">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredItems.length === 0 ? (
                <tr><td colSpan={6} className="px-6 py-12 text-center text-gray-500">No inventory items found</td></tr>
              ) : (
                filteredItems.map((item) => {
                  const isLowStock = item.current_stock <= item.reorder_point;
                  return (
                    <tr key={item.id} className={isLowStock ? 'bg-red-50' : 'hover:bg-gray-50'}>
                      <td className="px-6 py-4 text-sm font-medium">{item.sku || '-'}</td>
                      <td className="px-6 py-4 text-sm">{item.catalog_item?.name || 'Custom Item'}</td>
                      <td className="px-6 py-4 text-sm font-bold" style={{ color: isLowStock ? '#dc2626' : '#111827' }}>
                        {item.current_stock} {item.unit_of_measure}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">{item.reorder_point}</td>
                      <td className="px-6 py-4 text-sm text-gray-500">{item.location}</td>
                      <td className="px-6 py-4 text-right">
                        <div className="flex justify-end gap-2">
                          <button onClick={() => openTransactionModal(item, 'in')} className="rounded bg-green-100 px-3 py-1 text-xs font-semibold text-green-700 hover:bg-green-200" title="Stock IN">
                            ➕ IN
                          </button>
                          <button onClick={() => openTransactionModal(item, 'out')} className="rounded bg-orange-100 px-3 py-1 text-xs font-semibold text-orange-700 hover:bg-orange-200" title="Stock OUT">
                            ➖ OUT
                          </button>
                          <button onClick={() => openTransactionModal(item, 'adjustment')} className="rounded bg-blue-100 px-3 py-1 text-xs font-semibold text-blue-700 hover:bg-blue-200" title="Adjust">
                            ⚙️
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Alerts View */}
      {viewMode === 'alerts' && (
        <div className="space-y-4">
          {alerts.length === 0 ? (
            <div className="rounded-xl bg-white p-12 text-center shadow-sm ring-1 ring-gray-100">
              <div className="text-4xl mb-4">✅</div>
              <h3 className="text-lg font-semibold">No Active Alerts</h3>
              <p className="mt-2 text-sm text-gray-600">All stock levels are healthy</p>
            </div>
          ) : (
            alerts.map((alert) => (
              <div key={alert.id} className="rounded-xl border border-orange-200 bg-orange-50 p-4">
                <div className="flex items-start justify-between">
                  <div className="flex gap-3">
                    <span className="text-2xl">{alert.alert_type === 'out_of_stock' ? '🚨' : '⚠️'}</span>
                    <div>
                      <h4 className="font-semibold text-orange-900">{alert.inventory_item?.catalog_item?.name || 'Item'} - {alert.alert_type.replace('_', ' ').toUpperCase()}</h4>
                      <p className="mt-1 text-sm text-orange-700">{alert.alert_message}</p>
                      <p className="mt-2 text-xs text-orange-600">SKU: {alert.inventory_item?.sku || 'N/A'} | Location: {alert.inventory_item?.location}</p>
                    </div>
                  </div>
                  <button onClick={() => handleAcknowledgeAlert(alert.id)} className="rounded-lg bg-orange-600 px-4 py-2 text-sm font-semibold text-white hover:bg-orange-700">
                    Acknowledge
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Transactions History */}
      {viewMode === 'transactions' && (
        <div className="overflow-hidden rounded-xl bg-white shadow-sm ring-1 ring-gray-100">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase text-gray-500">Date</th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase text-gray-500">Type</th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase text-gray-500">Item</th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase text-gray-500">Quantity</th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase text-gray-500">Reference</th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase text-gray-500">Notes</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {transactions.length === 0 ? (
                <tr><td colSpan={6} className="px-6 py-12 text-center text-gray-500">No transactions recorded</td></tr>
              ) : (
                transactions.map((txn) => (
                  <tr key={txn.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 text-sm text-gray-500">{new Date(txn.created_at).toLocaleDateString()}</td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex rounded-full px-2 py-1 text-xs font-semibold ${
                        txn.transaction_type === 'in' ? 'bg-green-100 text-green-800' :
                        txn.transaction_type === 'out' ? 'bg-orange-100 text-orange-800' : 'bg-blue-100 text-blue-800'
                      }`}>
                        {txn.transaction_type.toUpperCase()}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm">{txn.inventory_item?.catalog_item?.name || 'N/A'}</td>
                    <td className="px-6 py-4 text-sm font-medium">{txn.quantity > 0 ? '+' : ''}{txn.quantity}</td>
                    <td className="px-6 py-4 text-sm text-gray-500">{txn.reference_type || '-'}{txn.reference_id ? `: ${txn.reference_id}` : ''}</td>
                    <td className="px-6 py-4 text-sm text-gray-500">{txn.notes || '-'}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Add Inventory Modal */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-2xl rounded-xl bg-white p-6 shadow-xl">
            <h3 className="mb-4 text-lg font-semibold">Add Inventory Item</h3>
            <form onSubmit={handleCreateInventoryItem} className="space-y-4">
              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <label className="mb-2 block text-sm font-medium">SKU</label>
                  <div className="flex gap-2">
                    <input type="text" value={formData.sku || ''} onChange={(e) => setFormData({ ...formData, sku: e.target.value })} className="flex-1 rounded-lg border px-4 py-2" placeholder="Enter SKU" />
                    <button type="button" onClick={() => setShowBarcodeScanner(true)} className="rounded-lg border border-gray-300 px-3 py-2 hover:bg-gray-50" title="Scan Barcode">
                      📷
                    </button>
                  </div>
                </div>
                <div>
                  <label className="mb-2 block text-sm font-medium">Current Stock</label>
                  <input type="number" value={formData.current_stock || 0} onChange={(e) => setFormData({ ...formData, current_stock: parseInt(e.target.value) })} className="w-full rounded-lg border px-4 py-2" />
                </div>
                <div>
                  <label className="mb-2 block text-sm font-medium">Reorder Point</label>
                  <input type="number" value={formData.reorder_point || 0} onChange={(e) => setFormData({ ...formData, reorder_point: parseInt(e.target.value) })} className="w-full rounded-lg border px-4 py-2" />
                </div>
                <div>
                  <label className="mb-2 block text-sm font-medium">Reorder Quantity</label>
                  <input type="number" value={formData.reorder_quantity || 0} onChange={(e) => setFormData({ ...formData, reorder_quantity: parseInt(e.target.value) })} className="w-full rounded-lg border px-4 py-2" />
                </div>
                <div>
                  <label className="mb-2 block text-sm font-medium">Location</label>
                  <input type="text" value={formData.location || ''} onChange={(e) => setFormData({ ...formData, location: e.target.value })} className="w-full rounded-lg border px-4 py-2" />
                </div>
                <div>
                  <label className="mb-2 block text-sm font-medium">Unit of Measure</label>
                  <input type="text" value={formData.unit_of_measure || ''} onChange={(e) => setFormData({ ...formData, unit_of_measure: e.target.value })} className="w-full rounded-lg border px-4 py-2" placeholder="unit, kg, liters" />
                </div>
              </div>
              <div>
                <label className="mb-2 block text-sm font-medium">Notes</label>
                <textarea value={formData.notes || ''} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} rows={3} className="w-full rounded-lg border px-4 py-2" />
              </div>
              <div className="flex justify-end gap-3">
                <button type="button" onClick={() => setShowAddModal(false)} className="rounded-lg border px-4 py-2 text-sm font-semibold hover:bg-gray-50">Cancel</button>
                <button type="submit" className="rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-white hover:bg-primary/90">Create Item</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Transaction Modal */}
      {showTransactionModal && selectedItem && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-lg rounded-xl bg-white p-6 shadow-xl">
            <h3 className="mb-4 text-lg font-semibold">
              Record Stock {transactionType.toUpperCase()} - {selectedItem.catalog_item?.name || selectedItem.sku}
            </h3>
            <form onSubmit={handleRecordTransaction} className="space-y-4">
              <div>
                <label className="mb-2 block text-sm font-medium">Quantity {transactionType === 'out' ? '(will be deducted)' : ''}</label>
                <input type="number" value={transactionData.quantity || 0} onChange={(e) => setTransactionData({ ...transactionData, quantity: parseInt(e.target.value) })} className="w-full rounded-lg border px-4 py-2" required />
              </div>
              <div>
                <label className="mb-2 block text-sm font-medium">Unit Cost (optional)</label>
                <input type="number" step="0.01" value={transactionData.unit_cost || 0} onChange={(e) => setTransactionData({ ...transactionData, unit_cost: parseFloat(e.target.value) })} className="w-full rounded-lg border px-4 py-2" />
              </div>
              <div>
                <label className="mb-2 block text-sm font-medium">Reference Type</label>
                <select value={transactionData.reference_type || ''} onChange={(e) => setTransactionData({ ...transactionData, reference_type: e.target.value })} className="w-full rounded-lg border px-4 py-2">
                  <option value="">Select type...</option>
                  <option value="purchase_order">Purchase Order</option>
                  <option value="sale">Sale</option>
                  <option value="return">Return</option>
                  <option value="waste">Waste</option>
                  <option value="manual">Manual Count</option>
                </select>
              </div>
              <div>
                <label className="mb-2 block text-sm font-medium">Reference ID (optional)</label>
                <input type="text" value={transactionData.reference_id || ''} onChange={(e) => setTransactionData({ ...transactionData, reference_id: e.target.value })} className="w-full rounded-lg border px-4 py-2" placeholder="PO-12345, INV-67890" />
              </div>
              <div>
                <label className="mb-2 block text-sm font-medium">Notes</label>
                <textarea value={transactionData.notes || ''} onChange={(e) => setTransactionData({ ...transactionData, notes: e.target.value })} rows={3} className="w-full rounded-lg border px-4 py-2" />
              </div>
              <div className="flex justify-end gap-3">
                <button type="button" onClick={() => { setShowTransactionModal(false); setSelectedItem(null); }} className="rounded-lg border px-4 py-2 text-sm font-semibold hover:bg-gray-50">Cancel</button>
                <button type="submit" className="rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-white hover:bg-primary/90">Record Transaction</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Barcode Scanner */}
      <BarcodeScanner isOpen={showBarcodeScanner} onClose={() => setShowBarcodeScanner(false)} onScanSuccess={handleBarcodeScanned} />
    </div>
  );
};

export default InventoryTab;
